import type { GridColDef } from '@mui/x-data-grid';
import type { GetArAgingReportResponse } from '@vy/api-definition';
import { OUTSTANDING_BREAKDOWN_OPTIONS } from '@vy/api-definition';
import type { ItemOf } from '@vy/utils';
import { withDefault } from '@vy/utils';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';

const AR_AGING_REPORT_KEYS = Object.freeze({
  CUSTOMER_NAME: 'customerName',
  TOTAL_GROSS_AMOUNT: 'totalGrossAmount',
  UP_TO_ONE_MONTH: 'upToOneMonth',
  ONE_TO_TWO_MONTHS: 'oneToTwoMonths',
  TWO_TO_THREE_MONTHS: 'twoToThreeMonths',
  MORE_THAN_THREE_MONTHS: 'moreThanThreeMonths',
} satisfies Record<string, string>);

export const AR_AGING_REPORT_TABLE_COLUMNS: GridColDef<ItemOf<GetArAgingReportResponse['summary']>>[] = [
  {
    field: AR_AGING_REPORT_KEYS.CUSTOMER_NAME,
    headerName: 'Customer Breakdown',
    valueGetter: (_value, row) => row.customer.name,
    minWidth: 240,
    flex: 1,
    headerClassName: 'pl-6 border-r',
    cellClassName: 'border-r',
    renderCell: ({
      row: {
        customer: { id, name },
      },
    }) => (
      <VyTableCell
        value={id === '__totals__' ? <strong>{name}</strong> : name}
        containerClassName="justify-start pl-4"
      />
    ),
  },
  {
    field: AR_AGING_REPORT_KEYS.UP_TO_ONE_MONTH,
    headerName: '1-30',
    headerAlign: 'left',
    valueGetter: (_value, row) => withDefault(
      row.outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.UP_TO_ONE_MONTH]?.grossAmount,
      0,
    ),
    minWidth: 240,
    flex: 1,
    renderCell: ({ row: { outstandingBreakdown } }) => (
      <VyTableCell
        value={
          <Price>
            {withDefault(outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.UP_TO_ONE_MONTH]?.grossAmount, 0)}
          </Price>
        }
        containerClassName="justify-start"
      />
    ),
  },
  {
    field: AR_AGING_REPORT_KEYS.ONE_TO_TWO_MONTHS,
    headerName: '31-60',
    headerAlign: 'left',
    valueGetter: (_value, row) => withDefault(
      row.outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.ONE_TO_TWO_MONTHS]?.grossAmount,
      0,
    ),
    minWidth: 240,
    flex: 1,
    renderCell: ({ row: { outstandingBreakdown } }) => (
      <VyTableCell
        value={
          <Price>
            {withDefault(
              outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.ONE_TO_TWO_MONTHS]?.grossAmount,
              0,
            )}
          </Price>
        }
        containerClassName="justify-start"
      />
    ),
  },
  {
    field: AR_AGING_REPORT_KEYS.TWO_TO_THREE_MONTHS,
    headerName: '61-90',
    headerAlign: 'left',
    valueGetter: (_value, row) => withDefault(
      row.outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.TWO_TO_THREE_MONTHS]?.grossAmount,
      0,
    ),
    minWidth: 240,
    flex: 1,
    renderCell: ({ row: { outstandingBreakdown } }) => (
      <VyTableCell
        value={
          <Price>
            {withDefault(
              outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.TWO_TO_THREE_MONTHS]?.grossAmount,
              0,
            )}
          </Price>
        }
        containerClassName="justify-start"
      />
    ),
  },
  {
    field: AR_AGING_REPORT_KEYS.MORE_THAN_THREE_MONTHS,
    headerName: '91+',
    headerAlign: 'left',
    valueGetter: (_value, row) => withDefault(
      row.outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.MORE_THAN_THREE_MONTHS]?.grossAmount,
      0,
    ),
    minWidth: 240,
    flex: 1,
    renderCell: ({ row: { outstandingBreakdown } }) => (
      <VyTableCell
        value={
          <Price>
            {withDefault(
              outstandingBreakdown[OUTSTANDING_BREAKDOWN_OPTIONS.MORE_THAN_THREE_MONTHS]?.grossAmount,
              0,
            )}
          </Price>
        }
        containerClassName="justify-start"
      />
    ),
  },
  {
    field: AR_AGING_REPORT_KEYS.TOTAL_GROSS_AMOUNT,
    headerName: 'Total',
    headerAlign: 'left',
    headerClassName: 'border-l',
    cellClassName: 'border-l',
    valueGetter: (_value, row) => {
      const breakdown = row.outstandingBreakdown;

      return (
        withDefault(breakdown[OUTSTANDING_BREAKDOWN_OPTIONS.UP_TO_ONE_MONTH]?.grossAmount, 0) +
        withDefault(breakdown[OUTSTANDING_BREAKDOWN_OPTIONS.ONE_TO_TWO_MONTHS]?.grossAmount, 0) +
        withDefault(breakdown[OUTSTANDING_BREAKDOWN_OPTIONS.TWO_TO_THREE_MONTHS]?.grossAmount, 0) +
        withDefault(breakdown[OUTSTANDING_BREAKDOWN_OPTIONS.MORE_THAN_THREE_MONTHS]?.grossAmount, 0)
      );
    },
    minWidth: 240,
    flex: 1,
    renderCell: (params) => (
      <VyTableCell
        value={<Price>{params.value}</Price>}
        containerClassName="justify-start"
      />
    ),
  },
];
